import React, { useEffect, useState } from 'react';
import { Lightbulb, Loader2, Send, X, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

const CATEGORIES = [
  { key: 'new_kpi', label: 'A new KPI / mental-skill metric to track' },
  { key: 'data_field', label: 'A new data field on an existing form' },
  { key: 'chart_visual', label: 'A new chart or visual' },
  { key: 'workflow', label: 'A workflow / UX improvement' },
  { key: 'other', label: 'Something else' },
];

const STATUS_TONE = {
  new: { label: 'Received', className: 'bg-[#FAFAF9] text-[#57534E] border-[#E7E5E4]' },
  reviewing: { label: 'Reviewing', className: 'bg-[#FFF9F5] text-[#BC562B] border-[#BC562B]/30' },
  planned: { label: 'Planned', className: 'bg-[#F0F7F1] text-[#2C5530] border-[#2C5530]/30' },
  shipped: { label: 'Shipped', className: 'bg-[#2C5530] text-white border-[#2C5530]' },
  declined: { label: 'Not this time', className: 'bg-[#F5F5F4] text-[#A8A29E] border-[#E7E5E4]' },
};

const SuggestMetricPanel = () => {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('new_kpi');
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState([]);
  const [loadingMine, setLoadingMine] = useState(true);

  const loadMine = async () => {
    setLoadingMine(true);
    try {
      const { data } = await API.get('/locker/suggestions/mine');
      setMine(data);
    } catch {
      // silent — panel still works
    } finally {
      setLoadingMine(false);
    }
  };

  useEffect(() => { loadMine(); }, []);

  const canSubmit = title.trim().length >= 3 && details.trim().length >= 10 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await API.post('/locker/suggestions', {
        category,
        title: title.trim(),
        details: details.trim(),
      });
      toast.success('Sent to Scott. Thanks — every idea shapes the Locker.');
      setTitle('');
      setDetails('');
      setCategory('new_kpi');
      setOpen(false);
      loadMine();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send suggestion.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white border border-[#E7E5E4] border-l-4 border-l-[#E89B6E] p-5 md:p-6" data-testid="suggest-metric-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex items-start gap-3">
          <span className="w-9 h-9 rounded-full bg-[#FFF4ED] text-[#BC562B] flex items-center justify-center flex-shrink-0">
            <Lightbulb className="w-5 h-5" />
          </span>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#BC562B] mb-0.5">Shape the Locker</p>
            <h3 className="font-['Playfair_Display'] text-xl md:text-2xl text-[#1A1A1A]">Suggest a metric or improvement</h3>
            <p className="text-sm text-[#57534E] mt-1 max-w-xl">
              Something you wish you could log or see? Send it straight to Scott — he reads every one and the best ideas ship.
            </p>
          </div>
        </div>
        {!open && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 bg-[#BC562B] text-white text-xs uppercase tracking-wider font-semibold px-4 py-2 hover:bg-[#9A4520]"
            data-testid="suggest-metric-open"
          >
            <Lightbulb className="w-4 h-4" /> Suggest an idea
          </button>
        )}
      </div>

      {open && (
        <div className="mt-5 bg-[#FAFAF9] border border-[#E7E5E4] p-4 space-y-3" data-testid="suggest-metric-form">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[#737373] mb-1.5">What kind of idea is this?</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full bg-white border border-[#E7E5E4] px-2 py-2 text-sm focus:outline-none focus:border-[#1A2E1D]"
              data-testid="suggest-metric-category"
            >
              {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[#737373] mb-1.5">Title <span className="text-[#A8A29E] normal-case">· one line</span></label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={140}
              placeholder="e.g. Track a Confidence rating per hole"
              className="w-full bg-white border border-[#E7E5E4] px-2 py-2 text-sm focus:outline-none focus:border-[#1A2E1D]"
              data-testid="suggest-metric-title"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[#737373] mb-1.5">Tell Scott more <span className="text-[#A8A29E] normal-case">· what it is, why it matters, how you'd use it</span></label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={5}
              maxLength={4000}
              placeholder="I'd love to log how confident I felt over each shot on a 1–5 scale so I can compare it to my scores over time…"
              className="w-full bg-white border border-[#E7E5E4] px-2 py-2 text-sm focus:outline-none focus:border-[#1A2E1D]"
              data-testid="suggest-metric-details"
            />
            <p className="mt-1 text-[10px] text-[#A8A29E] tabular-nums">{details.length} / 4000</p>
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => { setOpen(false); setTitle(''); setDetails(''); setCategory('new_kpi'); }}
              className="inline-flex items-center gap-1.5 text-xs text-[#737373] hover:text-[#1A1A1A] px-3 py-2"
              data-testid="suggest-metric-cancel"
            >
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              className="inline-flex items-center gap-2 bg-[#2C5530] text-white text-xs uppercase tracking-wider font-semibold px-4 py-2 hover:bg-[#1E3A21] disabled:opacity-50"
              data-testid="suggest-metric-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send to Scott
            </button>
          </div>
        </div>
      )}

      {!loadingMine && mine.length > 0 && (
        <div className="mt-5 pt-4 border-t border-[#F2F0E9]" data-testid="suggest-metric-history">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#737373] mb-2 flex items-center gap-1.5">
            <Clock className="w-3 h-3" /> Your ideas
          </p>
          <ul className="space-y-2">
            {mine.slice(0, 5).map((s) => {
              const tone = STATUS_TONE[s.status] || STATUS_TONE.new;
              return (
                <li key={s.id} className="flex items-start justify-between gap-3 bg-[#FAFAF9] border border-[#E7E5E4] px-3 py-2" data-testid={`suggestion-row-${s.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#1A1A1A] truncate">{s.title}</p>
                    <p className="text-[10px] text-[#A8A29E] mt-0.5">
                      {new Date(s.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 border flex-shrink-0 ${tone.className}`}>
                    {s.status === 'shipped' && <CheckCircle2 className="w-3 h-3 inline-block mr-1 -mt-0.5" />}
                    {tone.label}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SuggestMetricPanel;
